import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { enablePushNotifications, pushConfigurationAvailable } from "@/lib/pushNotifications";

/**
 * Admin and Analyst signal push is opt-in at the browser permission layer,
 * but once permission is granted FI Radar keeps their signal preference enabled
 * and automatically registers the current device on authenticated startup.
 */
export function AutoPushEnrollment() {
  const { user, roles, isAuthenticated, isAuthorizing } = useAuth();
  const attemptedUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user || isAuthorizing || !pushConfigurationAvailable()) return;

    const privileged = roles.some((role) => {
      const name = role.role_name.toLowerCase();
      return name === "admin" || name === "analyst";
    });
    if (!privileged || attemptedUserRef.current === user.id) return;

    attemptedUserRef.current = user.id;
    let cancelled = false;

    void (async () => {
      try {
        if (cancelled) return;

        // Do not repeatedly prompt a user who has already denied permission.
        if (typeof Notification !== "undefined" && Notification.permission === "denied") return;

        await enablePushNotifications(user.id);
      } catch (error) {
        // Automatic enrollment must never block or disrupt application startup.
        console.info("[push] automatic admin/analyst enrollment skipped:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isAuthorizing, roles, user]);

  return null;
}
